import { useState, useEffect, useCallback } from 'react';
import { prefetchWorkspaceCompanies } from '../utils/workspaceCompanies';

/**
 * Enterprise membership for the signed-in user.
 * Uses the shared workspace-companies fetch; refetches on window focus.
 *
 * - isEnterpriseAdmin: at least one active membership as owner or admin
 * - isEnterpriseUser: at least one active membership as a plain member
 *   (and no admin membership — admin supersedes for badge/display)
 */
export function useIsEnterpriseAdmin() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isUser, setIsUser] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchMembership = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsAdmin(false);
      setIsUser(false);
      setLoading(false);
      return;
    }
    try {
      const list = await prefetchWorkspaceCompanies();
      const hasAdmin = list.some((e) => e.isAdmin);
      const hasUser = list.some((e) => e.role && !e.isAdmin);
      setIsAdmin(hasAdmin);
      // Admin is the stronger label; only surface "Enterprise User" when that is the peak role.
      setIsUser(hasUser && !hasAdmin);
    } catch {
      setIsAdmin(false);
      setIsUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembership();
  }, [fetchMembership]);

  useEffect(() => {
    const onFocus = () => {
      if (localStorage.getItem('token')) fetchMembership();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchMembership]);

  return { isEnterpriseAdmin: isAdmin, isEnterpriseUser: isUser, loading };
}

export default useIsEnterpriseAdmin;
